/**
 * Content Service
 * Unified module caching using kassie_module_{moduleId} pattern.
 * Each module gets one localStorage key with all its data.
 */

import { api } from './api.js';
import i18next from 'i18next';

// ── Module Cache Helpers ────────────────────────────────────────────────────

const MODULE_PREFIX = 'kassie_module_';

/**
 * Get a cached module from localStorage
 * @param {string} moduleId
 * @returns {object|null}
 */
export function getCachedModule(moduleId) {
  try {
    const raw = localStorage.getItem(`${MODULE_PREFIX}${moduleId}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Store/update a module in localStorage
 * Merges new data with existing cached data (preserves story/wordSearch/music blocks)
 * @param {string} moduleId
 * @param {object} data
 */
export function setCachedModule(moduleId, data) {
  try {
    const existing = getCachedModule(moduleId) || {};
    const merged = { ...existing, ...data, cachedAt: new Date().toISOString() };
    localStorage.setItem(`${MODULE_PREFIX}${moduleId}`, JSON.stringify(merged));
  } catch (e) {
    console.warn(`Could not cache module ${moduleId}:`, e);
  }
}

/**
 * Get all cached modules from localStorage
 * @returns {object[]}
 */
export function getAllCachedModules() {
  const modules = [];
  try {
    const keys = Object.keys(localStorage);
    for (const key of keys) {
      if (key.startsWith(MODULE_PREFIX)) {
        const raw = localStorage.getItem(key);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && parsed.id) {
            modules.push(parsed);
          }
        }
      }
    }
  } catch {
    // Ignore
  }
  return modules;
}

// ── Content Loading ─────────────────────────────────────────────────────────

/**
 * Get story content for a module.
 * Reads from kassie_module_{id}.story if cached, fetches from API if not.
 */
export async function loadStoryContent(moduleId, options = {}) {
  try {
    console.log(`📖 Loading story content for module: ${moduleId}`);

    // Try API first when online
    try {
      const { getDeviceId } = await import('./deviceAuth.js');
      const deviceId = getDeviceId();

      const storyData = await api.get(`/api/device/modules/${moduleId}/story`, {
        deviceId: deviceId
      });

      // PREVIEW MODE: Skip cache
      if (options.skipCache) {
        return storyData;
      }

      // Cache story data into the unified module key
      const cached = getCachedModule(moduleId);
      const cachedVersion = cached?.story?.version;
      const apiVersion = storyData.version;

      // Update cache with fresh story data
      setCachedModule(moduleId, {
        story: storyData,
        fetch_complete: true,
        latest: cachedVersion === apiVersion ? cached?.latest : true
      });

      console.log(`✅ Story content loaded: ${storyData.pages?.length || 0} pages`);
      return storyData;
    } catch (apiError) {
      console.warn('⚠️ API request failed:', apiError.message);

      if (apiError.message.includes('404')) {
        throw new Error('CONTENT_NOT_READY');
      }

      if (options.skipCache) {
        throw new Error('Preview content not available');
      }

      // Fallback to cached story
      const cached = getCachedModule(moduleId);
      if (cached?.story) {
        console.log('✅ Loaded story from cache');
        return cached.story;
      }

      throw new Error('Story not available offline');
    }
  } catch (error) {
    console.error('❌ Failed to load story content:', error);
    throw error;
  }
}

/**
 * Get available content for a child profile.
 * Fetches catalog from API and stores each module individually.
 * Falls back to scanning cached modules when offline.
 */
export async function getProfileContent(profileId) {
  try {
    console.log(`📚 Loading content for profile: ${profileId}`);
    const currentLanguage = i18next.language || 'af';

    // If online, fetch from API and update cache
    if (navigator.onLine) {
      try {
        const modules = await _fetchAndCacheModules(profileId, currentLanguage);
        return modules;
      } catch (err) {
        console.warn('⚠️ API fetch failed, falling back to cache:', err.message);
      }
    }

    // Offline or API failed — read from cached modules
    const cached = getAllCachedModules();
    if (cached.length > 0) {
      console.log(`⚡ Serving ${cached.length} modules from cache (offline)`);
      return cached;
    }

    throw new Error('No content available offline');
  } catch (error) {
    console.error('❌ Failed to load profile content:', error);
    throw error;
  }
}

/**
 * Fetch catalog from API and store each module individually.
 * @private
 */
async function _fetchAndCacheModules(profileId, language) {
  let familyId = null;
  try {
    const storedFamily = localStorage.getItem('authorizedFamily');
    if (storedFamily) {
      const family = JSON.parse(storedFamily);
      familyId = family.id || family.familyId || null;
    }
  } catch { /* ignore */ }

  const response = await api.get('/api/content/catalog', {
    profile_id: profileId,
    family_id: familyId,
    language: language
  });

  const rawModules = response.modules || response || [];
  const modules = (rawModules.modules || rawModules || []);

  const result = [];

  for (const module of modules) {
    const transformed = {
      id: module.id,
      title: module.title,
      description: module.description,
      thumbnailUrl: module.thumbnailUrl,
      coverImageUrl: module.coverImageUrl,
      category: module.category && typeof module.category === 'object'
        ? {
          id: module.category.id,
          name: module.category.name,
          borderColor: module.category.borderColor,
          iconUrl: module.category.iconUrl,
          badgeUrl: module.category.badgeUrl
        }
        : module.category,
      activityType: module.activityType,
      type: module.type || null,
      gameConfig: module.gameConfig || null,
      ageMin: module.ageRange?.min || module.ageMin || 0,
      ageMax: module.ageRange?.max || module.ageMax || 18,
      isFree: module.isFree,
      tokenCost: module.tokenCost,
      hasEntitlement: module.canAccess || module.hasEntitlement || false,
      accessStatus: module.accessStatus,
      purchaseInfo: module.purchaseInfo,
      estimatedDuration: module.estimatedDuration || 10,
      hasPublisherAudio: module.hasPublisherAudio || false,
      hasFamilyRecording: module.hasFamilyRecording || false,
      // Music fields
      audioUrl: module.audioUrl || null,
      artistName: module.artistName || null,
      songDuration: module.songDuration || null,
      lyricsUrl: module.lyricsUrl || null,
      syncedLyricsUrl: module.syncedLyricsUrl || null,
      // Version from API (if available)
      version: module.version || null
    };

    // Check if cached version matches — set latest flag
    const existing = getCachedModule(module.id);
    const isLatest = existing?.version && transformed.version
      ? existing.version === transformed.version
      : false;

    // Merge: keep existing story/wordSearch/music data, update metadata
    setCachedModule(module.id, {
      ...transformed,
      latest: isLatest,
      fetch_complete: existing?.fetch_complete || false,
      // Preserve content blocks from existing cache
      story: existing?.story || null,
      wordSearch: existing?.wordSearch || null,
      music: existing?.music || null
    });

    // Return the full cached module (with content blocks if available)
    const full = getCachedModule(module.id);
    result.push(full);
  }

  console.log(`✅ Cached ${result.length} modules individually`);
  return result;
}

// ── Progress Tracking ───────────────────────────────────────────────────────

/**
 * Track reading progress
 */
export async function trackProgress(profileId, moduleId, pageId, action, timeSpent, metadata = {}) {
  try {
    const progressData = {
      profileId,
      moduleId,
      pageId,
      action,
      timestamp: new Date().toISOString(),
      timeSpent,
      metadata
    };

    console.log(`📊 Tracking progress:`, progressData);

    try {
      const { getDeviceId } = await import('./deviceAuth.js');
      const deviceId = getDeviceId();

      let activityType;
      let itemId;

      if (action === 'story_started' || action === 'page_completed' || action === 'story_completed') {
        activityType = 'story-page';
        if (action === 'story_started') {
          itemId = '1';
        } else {
          const pageMatch = progressData.pageId?.match(/(?:page-)?(\d+)/);
          itemId = pageMatch ? pageMatch[1] : '1';
        }
      } else if (action === 'game_completed' || action === 'game_started') {
        activityType = 'game';
        itemId = progressData.moduleId;
      } else {
        activityType = 'story-page';
        itemId = '1';
      }

      const params = new URLSearchParams({
        deviceId: deviceId,
        module_id: progressData.moduleId,
        activity_type: activityType,
        item_id: itemId,
        time_spent_seconds: (progressData.timeSpent || 0).toString()
      });

      if (progressData.metadata?.score !== undefined) {
        params.append('score', progressData.metadata.score.toString());
      }

      await api.post(`/api/device/profiles/${profileId}/progress?${params.toString()}`);
      console.log('✅ Progress tracked successfully');
    } catch (apiError) {
      console.warn('⚠️ Could not sync progress, queuing for later:', apiError.message);
      _queueProgress(profileId, progressData);
    }

    return true;
  } catch (error) {
    console.error('❌ Failed to track progress:', error);
    return false;
  }
}

/**
 * Queue progress for offline sync
 * @private
 */
function _queueProgress(profileId, data) {
  try {
    const key = `kassie_progress_queue_${profileId}`;
    const queue = JSON.parse(localStorage.getItem(key) || '[]');
    queue.push(data);
    localStorage.setItem(key, JSON.stringify(queue));
  } catch { /* ignore */ }
}

// ── Stars ───────────────────────────────────────────────────────────────────

/**
 * Award stars for completion
 */
export async function awardStars(profileId, amount, reason) {
  try {
    console.log(`⭐ Awarding ${amount} stars for: ${reason}`);

    const { getDeviceId } = await import('./deviceAuth.js');
    const deviceId = getDeviceId();

    const params = new URLSearchParams({
      profile_id: profileId,
      amount: amount.toString(),
      achievement_type: reason === 'story_completed' ? 'story-complete' :
        reason === 'game_completed' ? 'game-complete' : reason,
      deviceId: deviceId
    });

    const result = await api.post(`/api/device/stars/award?${params.toString()}`);
    console.log('✅ Stars awarded successfully');
    return result;
  } catch (apiError) {
    console.warn('⚠️ Could not award stars, queuing for later:', apiError.message);
    try {
      const key = `kassie_stars_queue_${profileId}`;
      const queue = JSON.parse(localStorage.getItem(key) || '[]');
      queue.push({ amount, reason, timestamp: new Date().toISOString() });
      localStorage.setItem(key, JSON.stringify(queue));
    } catch { /* ignore */ }
    return { success: true, queued: true };
  }
}

/**
 * Get star balance for profile
 */
export async function getStarBalance(profileId) {
  try {
    const { getDeviceId } = await import('./deviceAuth.js');
    const deviceId = getDeviceId();

    const result = await api.get(`/api/device/profiles/${profileId}/stars`, {
      deviceId: deviceId
    });
    return result.balance || 0;
  } catch {
    return 0;
  }
}
